<script setup lang="ts">
import { MeasureResult } from '@Shared/utils/types';
import { roundAwayFromZero } from '@Shared/utils/util';
import VisualDelta from '@Shared/components/visuals/parts/VisualDelta.vue';
import { useFilterStore } from '@Portal/stores/filter';
import { storeToRefs } from 'pinia';
import { getImageUrl } from '@Shared/components/visuals/utility';

interface SocialReviewRatingsViewModel {
  title: string;
  starValue?: MeasureResult;
  starValueBreakdown: MeasureResult[];
  count: number;
}

const maxNumStars = 5;
const props = defineProps<SocialReviewRatingsViewModel>();
const { starValue, starValueBreakdown, count } = toRefs(props);

const roundedStarAverage = computed<number | undefined>(() =>
  starValue.value?.currentValue != undefined ? roundAwayFromZero(starValue.value.currentValue, 1) : undefined
);

const roundedStarAverageForImage = computed<number | undefined>(() =>
  starValue.value?.currentValue != undefined ? roundAwayFromZero(starValue.value.currentValue) : undefined
);

const roundedDeltaValue = computed<number | undefined>(() => {
  if (
    !roundedStarAverage.value ||
    roundedStarAverage.value == undefined ||
    !starValue.value ||
    starValue.value.previousValue == undefined
  )
    return undefined;

  // due to floating-point arithmetic we need to round after the subtraction to ensure the decimal places
  return roundAwayFromZero(roundedStarAverage.value - roundAwayFromZero(starValue.value.previousValue, 1), 1);
});

const maxBreakdownWidth = 200;
const maxBreakdownCount = computed<number>(() =>
  starValueBreakdown.value.reduce((acc, curr) => (acc < (curr.currentValue ?? 0) ? (curr.currentValue ?? 0) : acc), 0)
);

const filterStore = useFilterStore();
const { dateFilterDisplayValue } = storeToRefs(filterStore);
</script>

<template>
  <div class="review-ratings stretch-height" v-if="count != 0">
    <div class="visual-title">Ratings {{ dateFilterDisplayValue }}</div>
    <div class="data-section">
      <div class="left-section">
        <div class="rating-box">
          <div class="rating-value" :class="{ 'rating-value-offset': roundedDeltaValue != undefined }">
            {{ roundedStarAverage }}
          </div>
          <VisualDelta v-if="starValue?.previousValue != undefined" :value="roundedDeltaValue!"></VisualDelta>
        </div>
        <div class="star-section" ng-style="{ '--gaps': dvm.gaps, '--stars': dvm.stars }">
          <div class="star-section-background">
            <img
              class="star"
              :src="getImageUrl('/social/Star_Unfilled.svg')"
              v-for="(star, index) in maxNumStars"
              :key="index"
            />
          </div>
          <div class="star-section-foreground">
            <img
              class="star"
              :src="getImageUrl('/social/Star_Filled.svg')"
              v-for="(_, index) in roundedStarAverageForImage"
              :key="index"
            />
          </div>
        </div>
        <div class="response-count">{{ count }} {{ count == 1 ? 'Review' : 'Reviews' }}</div>
      </div>
      <div class="right-section">
        <div v-for="(value, index) in starValueBreakdown" :key="index" class="star-breakdown-section">
          <div class="star-breakdown-label">{{ value.label }}</div>
          <img class="star-breakdown-image" :src="getImageUrl('/social/RatingBreakdownStar.svg')" />
          <div
            :style="{
              width: ((value.currentValue ?? 0) / maxBreakdownCount) * maxBreakdownWidth + 'px',
              'border-radius': value.currentValue == maxBreakdownCount ? '5px' : '5px 0 0 5px',
            }"
            class="star-breakdown-line star-breakdown-coloured-line"
          ></div>
          <div
            :style="{
              width: maxBreakdownWidth - ((value.currentValue ?? 0) / maxBreakdownCount) * maxBreakdownWidth + 'px',
              'border-radius': !value.currentValue || value.currentValue == 0 ? '5px' : '0 5px 5px 0',
            }"
            class="star-breakdown-line star-breakdown-baseline"
          ></div>
          <div class="star-breakdown-count">{{ value.currentValue }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.review-ratings {
  background-color: white;
  padding: 16px;
}

.review-ratings .visual-title {
  padding-top: 0 !important;
  padding-left: 0 !important;
}

.data-section {
  padding-top: 23px;
  display: flex;
}

.left-section {
  display: flex;
  flex-wrap: wrap;
  width: 40%;
  flex-direction: column;
  align-items: center;
}

.rating-box {
  margin-top: auto;
  display: flex;
  align-items: flex-end;
}

.rating-value {
  font-weight: 700;
  font-size: 65px;
  line-height: 65px;
  color: #4a4a4a;
  text-align: center;
  display: inline-block;
  padding-bottom: 10px;
}

.rating-value-offset {
  margin-left: 10px;
  padding-right: 5px;
}

.star-section {
  position: relative;
  display: inline-block;
  --gap-size: 8px;
  --star-size: 26px;
}

// we display the forground & the background of the stars ontop of each other by utilising position: absolute
.star-section-background {
  display: inline-flex;
}

.star-section-background > :not(:first-child),
.star-section-foreground > :not(:first-child) {
  // support older versions of safari by using margin-left instead of gap.
  // We don't add it on the first child to get middle alignment.
  margin-left: var(--gap-size);
}

.star-section-foreground {
  position: absolute;
  overflow: hidden;
  display: flex;
  left: 0;
  top: 0;
}

.star-section-foreground {
  width: 'calc(var(--gaps) * var(--gap-size) + var(--stars) * var(--star-size))';
}

.star {
  width: 26px;
  flex-shrink: 0;
}

.response-count {
  font-size: 16px;
  line-height: 200%;
  text-align: center;
  color: #4a4a4a;
  padding-top: 12px;
}

.right-section {
  display: flex;
  flex-direction: column;
  width: 60%;
  align-items: flex-start;
  padding-left: 20px;
}

.star-breakdown-section {
  display: flex;
  align-items: baseline;
}

.star-breakdown-label {
  font-weight: 600;
  font-size: 16px;
  line-height: 200%;
  color: #4a4a4a;
  display: inline-block;
  padding-right: 8px;
}

.star-breakdown-image {
  margin: auto;
  margin-right: 8px;
}

.star-breakdown-line {
  height: 8px;
}
.star-breakdown-baseline {
  background: #cecece;
}

.star-breakdown-coloured-line {
  background: #f4b400;
}

.star-breakdown-count {
  font-size: 11px;
  line-height: 185%;
  color: #4a4a4a;
  display: inline-block;
  margin-left: 8px;
}

@media only screen and (max-width: 889px) {
  .review-ratings {
    padding: 8px;
  }

  .review-ratings .visual-title {
    padding-top: 8px !important;
    padding-left: 8px !important;
  }
}

@media only screen and (max-width: 893px) and (min-width: 769px) {
  .star-section {
    --gap-size: 3px;
  }

  .star-breakdown-label {
    padding-right: 4px;
  }

  .star-breakdown-image {
    margin-right: 5px;
  }

  .star-breakdown-count {
    margin-left: 3px;
  }

  .right-section {
    padding-left: 1px;
  }

  .review-ratings {
    padding: 2px;
  }

  .review-ratings .visual-title {
    padding-top: 13px !important;
    padding-left: 13px !important;
  }
}

@media only screen and (max-width: 861px) and (min-width: 769px) {
  .rating-value-offset {
    margin-left: 23px;
    padding-right: 0px;
  }
}

@media only screen and (max-width: 791px) and (min-width: 769px) {
  .star-breakdown-label {
    padding-right: 2px;
  }

  .star-breakdown-image {
    margin-right: 2px;
  }

  .star-breakdown-count {
    margin-left: 0;
  }
}

@media only screen and (max-width: 528px) {
  .star-breakdown-label {
    padding-right: 4px;
  }

  .star-breakdown-image {
    margin-right: 5px;
  }

  .star-breakdown-count {
    margin-left: 3px;
  }

  .right-section {
    padding-left: 1px;
  }
}

@media only screen and (max-width: 482px) {
  .star-section {
    --gap-size: 3px;
  }
}

@media only screen and (max-width: 480px) {
  .rating-value {
    font-weight: 900;
    font-size: 50px;
    line-height: 50px;
  }

  .rating-value-offset {
    margin-left: 5px;
  }

  .left-section {
    width: 39%;
  }

  .star-breakdown-image {
    margin-right: 4px;
  }
}

@media only screen and (max-width: 472px) {
  .data-section {
    flex-direction: column;
  }

  .left-section {
    width: 100%;
  }

  .right-section {
    width: auto;
    margin: auto;
  }
}

@media only screen and (max-width: 321px) {
  .rating-value {
    font-size: 50px;
  }

  .rating-value-offset {
    padding-right: 5px;
  }
}

@media only screen and (max-width: 313px) {
  .star-breakdown-label {
    font-size: 14px;
    padding-right: 0px;
  }

  .star-breakdown-image {
    margin-right: 1px;
  }

  .star-breakdown-count {
    margin-left: 3px;
  }
}

@media only screen and (max-width: 289px) {
  .star-breakdown-label {
    font-size: 10px;
  }

  .star-breakdown-image {
    width: 9px;
    margin-right: 0;
  }

  .star-breakdown-count {
    margin-left: 0;
  }

  .star-breakdown-line {
    height: 6px;
  }
}
</style>
